import React, { useMemo } from "react";
import SearchableSelect, { SearchableSelectOption } from "../DataEntry/SearchableSelect";
import { IngredientAlias } from "../../types/mes";

interface IngredientAliasPickerProps {
  /** Full alias codebook to choose from. */
  aliases: IngredientAlias[];
  /** Currently linked alias IDs. */
  value: number[];
  onChange: (aliasIds: number[]) => void;
  disabled?: boolean;
}

/**
 * Bira aliase (Ritam artikle) koji pripadaju jednoj sirovini.
 *
 * Isti alias smije stajati na vise sirovina, pa se vec zauzeti aliasi ne
 * izbacuju iz popisa — samo se oznaci na koliko ih sirovina jos visi, da se
 * vidi kad se dijeli.
 */
const IngredientAliasPicker: React.FC<IngredientAliasPickerProps> = ({
  aliases,
  value,
  onChange,
  disabled = false,
}) => {
  const byId = useMemo(() => new Map(aliases.map((a) => [a.ID, a])), [aliases]);

  const options: SearchableSelectOption[] = useMemo(
    () =>
      aliases
        .filter((a) => !value.includes(a.ID))
        .map((a) => {
          const shared = (a.Ingredients?.length ?? 0) > 0;
          return {
            value: a.ID,
            label: a.AliasName,
            sublabel: [a.ExternalCode, shared ? `već na ${a.Ingredients!.length} sirovini/a` : null]
              .filter(Boolean)
              .join(" · ") || undefined,
          };
        }),
    [aliases, value]
  );

  return (
    <div className="alias-picker">
      <SearchableSelect
        value={0}
        options={options}
        onChange={(aliasId) => aliasId && onChange([...value, aliasId])}
        placeholder="Pretraži Ritam artikle..."
        disabled={disabled}
        allowClear={false}
      />
      {value.length > 0 && (
        <ul className="alias-picker-list">
          {value.map((id) => {
            const alias = byId.get(id);
            return (
              <li key={id}>
                <span className="alias-picker-name">{alias?.AliasName ?? `Alias #${id}`}</span>
                {alias?.ExternalCode && <span className="alias-picker-code">{alias.ExternalCode}</span>}
                <button
                  type="button"
                  className="alias-picker-remove"
                  onClick={() => onChange(value.filter((v) => v !== id))}
                  disabled={disabled}
                  aria-label={`Ukloni ${alias?.AliasName ?? id}`}
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default IngredientAliasPicker;
